import { ConfigV1 } from './config';
import { ConfigType, ConfigStatus } from './sensor-configuration';

export type Space = {
  external_id: string;
  name: string;
  ancestry: Array<{ id: string; name: string }>;
  organization_external_id: string;
  organization_name: string;
  time_zone: string;
  daily_reset: string;
  space_type: string;
  function: string;
  capacity: number;
  max_dwell_minutes: number;
  max_count_last_2_weeks: number;

  installations: Array<{
    id: string;
    serial_number: string;
    link_id: string;
    doorway_name: string;
    total_captures: number;
    total_accuracy: number;
    os_version: Sensor['os_version'];
    in_mpi: boolean;
    sensor_type: Sensor['sensor_type'];
    diagnostic_info_json: Sensor['diagnostic_info_json'];
  }>;
};

// This type rperesents the data that is reported up by the sensor on every heartbeat
type SensorDiagnosticInfo = Partial<{
  capture_status: {
    current_capture: number;
    current_num_event_retries: number;
    current_upload: number;
    finished: boolean;
    num_captures: number;
    num_event_retries: number;
    priority: boolean;
    total_dropped: number;
    total_retries: number;
    total_uploaded: number;
  };
  config: ConfigV1;
  date: string;
  fan_on: boolean;
  low_power_mode: boolean;
  network_addresses: Array<{
    address: string;
    family: string;
    if: string;
    mac: string;
  }>;
  noise_score: {
    score: number;
    stddev: number;
  };
  os: {
    ID: string;
    NAME: string;
    PRETTY_NAME: string;
    VERSION: string;
    VERSION_ID: string;
  };
}>;

type SensorCurrentStateMetadata = {
  human_readable_name: string;
  next_state_transition_at: string | null;
} & (
  | { requires_human_interaction: false }
  | { requires_human_interaction: true; latest_note_with_actions: Note }
);

export type Sensor = {
  serial_number: string;
  created_at: string;
  link_id: string;
  organization: {
    id: string;
    name: string;
  };
  spaces: Array<{
    id: string;
    name: string;
    ancestry: Array<{ id: string; name: string }>;
  }>;

  installation: {
    total_annotated_events: number;
  };

  doorway?: {
    id: string;
  };
  doorway_name: string | null;

  latest_accuracy?: number;

  latest_report?: {
    report_name: string;

    ingresses_annotated: number;
    ingresses_extra: number;
    ingresses_missed: number;

    egresses_annotated: number;
    egresses_extra: number;
    egresses_missed: number;

    egress_accuracy: number;
    ingress_accuracy: number;
    total_accuracy: number;
  };

  current_status: string;
  assigned_config: ConfigV1;
  assigned_config_template: ConfigType;
  config_status: ConfigStatus;
  first_heartbeat: string | null;
  last_heartbeat: string;
  last_counted: string;
  current_disposition: string;
  os_version: string | null;
  diagnostic_info_json: SensorDiagnosticInfo;

  captures: {
    total: number;
    by_status: {
      annotated: number;
      created: number;
      failed: number;
      frozen: number;
      mpi_pending_annotation: number;
      post_qa_score_complete: number;
      preparing_to_post_qa_score: number;
      preparing_to_score: number;
      preparing_to_send: number;
      processing_annotations: number;
      qa_complete: number;
      scored: number;
      unannotated: number;
      waiting_for_annotations: number;
      waiting_for_qa: number;
      mpi_scored: number;
      incorrectly_annotated: number;
    };
  };

  in_mpi: boolean;
  qaed_event_count: number;

  current_state: string;
  current_state_metadata: SensorCurrentStateMetadata;
  assigned_team: AssignableTeam | null;

  // NOTE: sensor_type is a deprecated field.
  sensor_type: string;
  kind: string | null;
  hardware_model: string | null;
  is_virtual: string | null;

  organization_name: string;
  organization_disposition: string;
  latest_install_date: string | null;
  first_heartbeat_after_latest_install_date: string | null;

  capture_priority_starts_at: string | null;
  capture_priority_ends_at: string | null;
} & (
  | {
      latest_config_change_id: null;
      latest_config_change_current_state: null;
      latest_config_change_completed_at: null;
      latest_config_change_new_config: null;
    }
  | {
      latest_config_change_id: string;
      latest_config_change_current_state: SensorConfigChangeState;
      latest_config_change_completed_at: string;
      latest_config_change_new_config: ConfigV1;
    }
);

export enum SensorType {
  OB1 = 'ob1',
  R56T = 'r56t',
  R60 = 'r60',
  S5 = 's5',
  VIRTUAL = 'virtual',
}

export enum AssignableTeam {
  ENGINEERING_IMPLEMENTATION_TEAM = 'ENGINEERING_IMPLEMENTATION_TEAM',
  CUSTOMER_SUCCESS_IMPLEMENTATION_TEAM = 'CUSTOMER_SUCCESS_IMPLEMENTATION_TEAM',
  CUSTOMER_SUCCESS_TEAM = 'CUSTOMER_SUCCESS_TEAM',
  EMBEDDED_TEAM = 'EMBEDDED_TEAM',
  ALGORITHM_TEAM = 'ALGORITHM_TEAM',
}
export const ASSIGNABLE_TEAM_TO_PRETTY_NAME = {
  [AssignableTeam.ENGINEERING_IMPLEMENTATION_TEAM]:
    'Engineering Implementation Team',
  [AssignableTeam.CUSTOMER_SUCCESS_IMPLEMENTATION_TEAM]:
    'Customer Success Implementation Team',
  [AssignableTeam.CUSTOMER_SUCCESS_TEAM]: 'Customer Success Team',
  [AssignableTeam.EMBEDDED_TEAM]: 'Embedded Team',
  [AssignableTeam.ALGORITHM_TEAM]: 'Algorithm Team',
};

export enum SensorConfigChangeState {
  IDLE = 'IDLE',
  COMMAND_IN_FLIGHT = 'COMMAND_IN_FLIGHT',
  COMMAND_RECEIVED = 'COMMAND_RECEIVED',
  WAITING_FOR_CONFIGS_TO_MATCH = 'WAITING_FOR_CONFIGS_TO_MATCH',
  INVALIDATING_ALGO_API = 'INVALIDATING_ALGO_API',
  COMPLETE = 'COMPLETE',
}

export type Capture = {
  id: number;
  tags: Array<{
    id: number;
    name: string;
    created_at: string;
    start_frame: number;
  }>;
  has_invalid_annotations?: boolean;
  has_invalid_algo_events?: boolean;
  latest_report: {
    id?: number;
    report_name?: string;
    completed_at?: string | null;
    egress_accuracy?: number | null;
    ingress_accuracy?: number | null;
    total_accuracy?: number | null;
    annotated_events?: Array<{
      id: any;
      direction: 1 | -1;
      seconds_offset: string;
      x_position: number;
      y_position: number;
    }>;
    algo_events?: Array<{
      id: any;
      direction: 1 | -1;
      seconds_offset: string;
      x_position: number;
      y_position: number;
    }>;
  } | null;
  annotation_status: CaptureStatus;
  created_at: string;
  video_starts_at: string;
  video_ends_at: string;
  low_integration_video_url: string | null;

  capture_url: string;
  inferences: any;
};
export enum CaptureStatus {
  failed = 'failed',
  created = 'created',

  preparing_to_send = 'preparing_to_send',
  waiting_for_annotations = 'waiting_for_annotations',
  mpi_pending_annotation = 'mpi_pending_annotation',

  processing_annotations = 'processing_annotations',
  annotated = 'annotated',

  preparing_to_score = 'preparing_to_score',
  scored = 'scored',
  mpi_scored = 'mpi_scored',
  waiting_for_qa = 'waiting_for_qa',
  qa_complete = 'qa_complete',

  preparing_to_post_qa_score = 'preparing_to_post_qa_score',
  post_qa_score_complete = 'post_qa_score_complete',

  frozen = 'frozen',
  incorrectly_annotated = 'incorrectly_annotated',
}
export const CAPTURE_STATUS_TO_PRETTY_NAME = {
  [CaptureStatus.failed]: ['Failed', 'Captures that failed to process.'],
  [CaptureStatus.frozen]: ['Frozen', 'Captures that were manually paused'],
  [CaptureStatus.created]: ['Created', 'Freshly created captures'],

  [CaptureStatus.preparing_to_send]: [
    'Preparing to send',
    'Captures to be sent for annotation',
  ],
  [CaptureStatus.waiting_for_annotations]: [
    'Waiting for annotation',
    'Captures currently being annotated',
  ],
  [CaptureStatus.mpi_pending_annotation]: [
    'MPI pending annotation',
    'Captures from MPIs currently being annotated',
  ],

  [CaptureStatus.processing_annotations]: [
    'Processing annotations',
    'Ingesting info from annotators into the system',
  ],
  [CaptureStatus.annotated]: ['Annotated', 'Captures that have been annotated'],

  [CaptureStatus.preparing_to_score]: [
    'Preparing to score',
    'Initial accuracy number calculation',
  ],
  [CaptureStatus.scored]: ['Scored', 'Captures w/ an initial accuracy score'],
  [CaptureStatus.mpi_scored]: [
    'MPI Scored',
    'Captures w/ a MPI initial accuracy score. This is the final state for captures in a MPI capture group.',
  ],
  [CaptureStatus.waiting_for_qa]: [
    'Waiting for QA',
    'Annotations are being checked over',
  ],
  [CaptureStatus.qa_complete]: [
    'QA complete',
    'Annotations should be correct.',
  ],
  [CaptureStatus.preparing_to_post_qa_score]: [
    'Preparing to post QA score',
    'Updated accuracy numbers are recalculating after QA',
  ],
  [CaptureStatus.post_qa_score_complete]: [
    'Post QA score complete',
    'Updated accuracy numbers have been calculated - this is the final state for captures not in a capture group.',
  ],
  [CaptureStatus.incorrectly_annotated]: [
    'Annotation identified as incorrect',
    'QA marked capture annotation as incorrect.',
  ],
};

export type Organization = {
  id: string;
  name: string;
  number_of_units: number;
  account_managers: Array<{
    id: string;
    slack_handle: string;
    email: string;
    full_name: string;
  }>;
};

export type MultiPointInstallation = {
  id: string;
  name: string;
  sensors: Array<MultiPointInstallationSensor>;
  config: {
    span_ms: number;
    max_x_offset_multiplier: number;
  };
  sensor_height: number;
  total_dedupe_accuracy: number;
  capture_group_count: number;
  min_video_starts_at: string;
  fully_annotated_capture_group_count: number;
  common_spaces: Array<{
    id: Space['external_id'];
    name: Space['name'];
  }>;
  organization_id: string;
  organization_name: string;
};

export type MultiPointInstallationSensor = {
  // Data in the "MultiPointInstallationSensor" table in the core api
  sensor_serial: string;
  position: number;
  spacing_right: number;
  overlap_right: number /* field of view overlap in px between this sensor and the one to its right */;
  multipoint_installation_id: MultiPointInstallation['id'];

  // Data added in the `super_mpi` materialized view
  config: ConfigV1;
  current_status: Sensor['current_status'];
  doorway_id: string | null;
  doorway_name: null;
  link_id: string | null;
  os_version: string | null;
  qaed_event_count: number;
  sensor_height: number;

  config_status: Sensor['config_status'];
  assigned_config_template: Sensor['assigned_config_template'];
  assigned_config: Sensor['assigned_config'];
  organization: Sensor['organization'];
  doorway: Sensor['doorway'];
  serial_number: Sensor['serial_number'];
  sensor_type: Sensor['sensor_type'];
  diagnostic_info_json: Sensor['diagnostic_info_json'];
};

export type CaptureGroup = {
  id: number;
  captures_annotated: number;
  annotated_events: number;
  fully_annotated_capture_count: number;
  total_accuracy: number;
  min_video_starts_at: string;
  captures: Array<{
    id: number;
    status: CaptureStatus;
    serial_number: string;
    has_invalid_annotations: boolean;
    annotations: Array<{
      id: number;
      direction: 1 | -1;
      seconds_offset: string;
      x_position: number;
      y_position: number;
    }>;
    algo_events: Array<{
      id: number;
      direction: 1 | -1;
      seconds_offset: string;
      x_position: number;
      y_position: number;
      is_dupe: boolean;
    }>;
    video_url: string;
    inferences: any;
    created_at: string;
    video_starts_at: string | null;
    video_ends_at: string | null;
    video_duration: number | null;
  }>;
};

export type MultiPointInstallationAccuracy = {
  created_at: string;
  completed_at: string;
  updated_at: string;
  config_json: unknown;
  total_dedupe_accuracy: number | null;
};

export type Note = {
  id: number;
  user: {
    full_name: string;
    email: string;
    picture: string | null;
  };
  type: string;
  data: { [key: string]: any };
  user_email: string;
  created_at: string;
  updated_at: string;
  user_editable: boolean;
  associated_metadata: { [key: string]: any };
  actions: Array<NoteAction>;
  is_collapsed_by_default: boolean;
};

// Represents an action that can be taken by a user on a note as a "quick next step"
export type NoteAction = {
  id: string;
  label: string;
  is_primary: boolean;
  enabled: boolean;
  href?: string;
  type: 'link' | 'client' | 'server';
};
